import { useTranslation } from 'react-i18next';

import SvgTelescope from '@/assets/widgets/empty-state-editor-note.svg';
import { Loader } from '@/components/loader/loader';
import { TextWidget } from '@/components/widgets/text-widget/text-widget';
import { useEditCompany } from '@/features/companies/hooks/use-edit-company.mutation';
import {
  COLUMN_1_WIDTH,
  COLUMN_WIDTH,
  GRID_GAP,
  WIDGET_EDITOR_NOTE_RANKING_HEIGHT,
  WIDGET_HEADER_MARGIN,
} from '@/features/companies/overview/utils/constants';
import { WidgetHeaderWithBackground } from '@/features/companies/overview/widget-header-with-background';
import { componentsPalette } from '@/theme/colors';

import { useCanEditCompany } from '../hooks/use-can-edit-company';

import { useCompany } from './use-company.query';
import { WidgetLoaderContainer } from './widget-loader-container';
import { SubHeaderBold } from '@/ui/typography/widgets';

const EDITOR_NOTE_MAX_WIDTH = COLUMN_1_WIDTH + GRID_GAP + COLUMN_WIDTH;

export const EditorNoteWidget = ({
  companyId,
  editable = true,
  variant = 'blue',
  height = WIDGET_EDITOR_NOTE_RANKING_HEIGHT,
  maxWidth = EDITOR_NOTE_MAX_WIDTH,
  className,
}: {
  companyId: string | null;
  editable?: boolean;
  variant?: 'blue' | 'white';
  height?: number;
  maxWidth?: number;
  className?: string;
}) => {
  const { t } = useTranslation('companies');
  const { mutateAsync, isPending } = useEditCompany();
  const { data: company, isLoading: isInitialLoading } = useCompany(companyId);

  const header =
    variant === 'blue' ? (
      <WidgetHeaderWithBackground
        margin={WIDGET_HEADER_MARGIN}
      >{t`company.editorNote`}</WidgetHeaderWithBackground>
    ) : (
      <SubHeaderBold>{t`company.editorNote`}</SubHeaderBold>
    );

  const canEditCompany = useCanEditCompany();
  const {
    backgroundColor,
    editModeBackgroundColor,
    editModeHeaderBackgroundColor,
    readMoreTextColor,
    editIconColor,
    editIconBackgroundColor,
  } = componentsPalette.company.editorNote[variant];

  if (isPending || isInitialLoading) {
    return (
      <WidgetLoaderContainer height={height} className={className}>
        <Loader />
      </WidgetLoaderContainer>
    );
  }

  if (!company) {
    return null;
  }

  const handleEdit = (editorNoteValue: string | null) => {
    return mutateAsync({
      editorNote: editorNoteValue ?? '',
    });
  };

  return (
    <TextWidget
      dataTestId="editor-note-widget"
      value={company.editorNote}
      height={height}
      disableBaseWidth
      maxWidth={maxWidth}
      disabled={!canEditCompany}
      editable={editable}
      className={className}
      onSave={handleEdit}
      header={header}
      placeholder={t`company.emptyState.editorNoteStatement`}
      backgroundColor={backgroundColor}
      editModeOptions={{
        backgroundColor: editModeBackgroundColor,
        header: (
          <WidgetHeaderWithBackground
            margin={WIDGET_HEADER_MARGIN}
            backgroundColor={editModeHeaderBackgroundColor}
          >{t`company.editorNote`}</WidgetHeaderWithBackground>
        ),
      }}
      editIconProps={{
        borderRadius: '100px',
        color: editIconColor,
        background: editIconBackgroundColor,
        padding: '6px 10px',
      }}
      emptyStateOptions={{
        label: t`company.emptyState.editorNoteStatement`,
        linkText: t`company.emptyState.clickToAdd`,
        image: SvgTelescope,
        backgroundColor: 'white',
        direction: 'column',
        labelAlign: 'center',
        maxWidth,
      }}
      bottomContentProps={{
        padding: '11px 0 0 0',
      }}
      readMoreProps={{
        color: readMoreTextColor,
      }}
    />
  );
};
